import type {
  UserAccess, User, SelectionInputs, SelectionResult, SelectionSession, SelectionQuota, Lesson, Submission, SubmissionMessage, AccountInfo, Subscription, AdminLessonQuestion, AdminSubmission, ChatMessage, ChatParticipant, QuizStat, AppNotification, PendingWork, AppConfig, AdminSettings, AppSettingsPatch, Plan, ContinueLesson, Badges, Invite, InviteList, InvitePreview, InviteRedeemResult, PaymentsConfig, CaptureResult, PlanQuote, AdminUsersPage, AdminUserDetail, AdminSelectionsPage, AdminSelectionDetail, AdminUserRow } from "../types";

const API_URL = import.meta.env.VITE_API_URL || "/api/v1";

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return res.json();
}

// ── Auth ──────────────────────────────────────────────────────────────────────

/** Sign in by approving on the other side (backend services/login_requests.py):
 *  "browser_via_telegram" — the website, approved in the bot;
 *  "miniapp_via_google" — the Mini App, approved in a browser signed in with Google. */
export type LoginKind = "browser_via_telegram" | "miniapp_via_google";

export const createLoginRequest = (kind: LoginKind) =>
  apiFetch<{ code: string; check: string; expires_at: string; link?: string }>("/auth/login-requests", {
    method: "POST", body: JSON.stringify({ kind }),
  });

/** 202 {status:"pending"} while waiting; the session once approved; throws 410 when gone. */
export const pollLoginRequest = (code: string) =>
  apiFetch<{ status?: string; access_token?: string }>("/auth/login-requests/poll", {
    method: "POST", body: JSON.stringify({ code }),
  });

export const peekLoginRequest = (code: string, token: string) =>
  apiFetch<{ check: string; device: string; created_at: string; kind: string; owner: string | null }>("/auth/login-requests/peek", {
    method: "POST", body: JSON.stringify({ code }),
  }, token);

export const approveLoginRequest = (code: string, token: string) =>
  apiFetch<{ status: string; access_token?: string }>("/auth/login-requests/approve", {
    method: "POST", body: JSON.stringify({ code }),
  }, token);

/** Link the other sign-in to THIS account (merging its account, if any). */
export const linkTelegram = (initData: string, token: string) =>
  apiFetch<{ status: string }>("/auth/link/telegram", {
    method: "POST", body: JSON.stringify({ init_data: initData }),
  }, token);

export const linkFirebase = (idToken: string, token: string) =>
  apiFetch<{ status: string }>("/auth/link/firebase", {
    method: "POST", body: JSON.stringify({ id_token: idToken }),
  }, token);

/** Linking what this place cannot prove: Telegram from the browser (bot),
 *  Google from the Mini App (a browser signed in with Google). */
export const createLinkRequest = (kind: "link_telegram" | "link_google", token: string) =>
  apiFetch<{ code: string; check: string; expires_at: string; link?: string }>("/auth/link-requests", {
    method: "POST", body: JSON.stringify({ kind }),
  }, token);

export const declineLoginRequest = (code: string) =>
  apiFetch<{ status: string }>("/auth/login-requests/decline", {
    method: "POST", body: JSON.stringify({ code }),
  });

export const loginTelegram = (initData: string) =>
  apiFetch<{ access_token: string }>("/auth/login/telegram", {
    method: "POST",
    body: JSON.stringify({ init_data: initData }),
  });

export const loginFirebase = (idToken: string) =>
  apiFetch<{ access_token: string }>("/auth/login/firebase", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken }),
  });

export const fetchMe = (token: string) =>
  apiFetch<User>("/auth/users/me", {}, token);

// ── Selection ─────────────────────────────────────────────────────────────────

export const recommendSelection = (inputs: SelectionInputs, token: string) =>
  apiFetch<SelectionResult>("/selection/recommend", {
    method: "POST",
    body: JSON.stringify(inputs),
  }, token);

export const previewSelection = (inputs: SelectionInputs, token: string) =>
  apiFetch<SelectionResult>("/selection/preview", {
    method: "POST",
    body: JSON.stringify(inputs),
  }, token);

export const fetchSelectionQuota = (token: string) =>
  apiFetch<SelectionQuota>("/selection/quota", {}, token);

export const fetchSelectionHistory = (
  token: string,
  params: { q?: string; date_from?: string; date_to?: string } = {},
) => {
  const qs = new URLSearchParams();
  if (params.q?.trim()) qs.set("q", params.q.trim());
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<SelectionSession[]>(`/selection/history${suffix}`, {}, token);
};

// ── Quiz stats ────────────────────────────────────────────────────────────────

export const recordQuizAnswer = (token: string, key: string, selected: number, isCorrect: boolean) =>
  apiFetch("/quiz/answer", {
    method: "POST",
    body: JSON.stringify({ key, selected, is_correct: isCorrect }),
  }, token).catch(() => { /* stats are best-effort */ });

export const fetchQuizStats = (token: string) =>
  apiFetch<QuizStat[]>("/admin/quiz/stats", {}, token);

// ── Lessons ───────────────────────────────────────────────────────────────────

export const fetchLessons = (token: string) =>
  apiFetch<Lesson[]>("/lessons", {}, token);

export const fetchLessonVideo = (lessonId: string, lang: string, token: string) =>
  apiFetch<{ url: string; aspect?: number | null }>(`/lessons/${lessonId}/video?lang=${lang}`, {}, token);

export const fetchLessonVideos = (lessonId: string, lang: string, token: string) =>
  apiFetch<{ videos: { url: string; aspect?: number | null; title?: string }[] }>(
    `/lessons/${lessonId}/videos?lang=${lang}`, {}, token);

// Mark a reading-only lesson (e.g. the intro) as done when it's opened.
export const completeLesson = (lessonId: string, token: string) =>
  apiFetch<{ status: string; newly_completed: boolean }>(`/lessons/${lessonId}/complete`, {
    method: "POST",
  }, token);

// Save the current video timecode (best-effort; called periodically by the player).
export const saveLessonPosition = (
  lessonId: string, position: number, duration: number | undefined, token: string,
) =>
  apiFetch(`/lessons/${lessonId}/position`, {
    method: "PUT",
    body: JSON.stringify({ position, duration }),
  }, token).catch(() => { /* progress saving is best-effort */ });

// Counts for the profile button: unread replies, and admin work awaiting review.
export const fetchBadges = (token: string) =>
  apiFetch<Badges>("/account/badges", {}, token);

// The lesson to resume on the main menu (or null if there's nothing to continue).
export const fetchContinueLesson = (token: string) =>
  apiFetch<ContinueLesson | null>("/lessons/continue", {}, token);

export const submitLessonFeedback = (lessonId: string, message: string, token: string) =>
  apiFetch("/lessons/" + lessonId + "/feedback", {
    method: "POST",
    body: JSON.stringify({ message }),
  }, token);

// ── Exercises ─────────────────────────────────────────────────────────────────

export const submitPhoto = (exerciseId: string, photoBase64: string, token: string) =>
  apiFetch<Submission>(`/exercises/${exerciseId}/submit`, {
    method: "POST",
    body: JSON.stringify({ photo_base64: photoBase64 }),
  }, token);

export const getMessages = (submissionId: string, token: string) =>
  apiFetch<SubmissionMessage[]>(`/submissions/${submissionId}/messages`, {}, token);

export const sendMessage = (submissionId: string, body: string, token: string, photoBase64?: string) =>
  apiFetch<SubmissionMessage>(`/submissions/${submissionId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body, photo_base64: photoBase64 }),
  }, token);

// Unified student chat per task — text question or photo work in one place.
export const exerciseChat = (exerciseId: string, body: string, token: string, photoBase64?: string) =>
  apiFetch<Submission>(`/exercises/${exerciseId}/chat`, {
    method: "POST",
    body: JSON.stringify({ body, photo_base64: photoBase64 }),
  }, token);

export const messagePhotoUrl = (submissionId: string, messageId: string, token: string) =>
  `${API_URL}/submissions/${submissionId}/messages/${messageId}/photo?token=${encodeURIComponent(token)}`;

// ── Per-participant chat ──────────────────────────────────────────────────────

export const fetchMyChat = (token: string) =>
  apiFetch<ChatMessage[]>("/chat", {}, token);

export const sendChat = (
  token: string,
  payload: { body?: string; photo_base64?: string; exercise_id?: string },
) => apiFetch<ChatMessage>("/chat", { method: "POST", body: JSON.stringify(payload) }, token);

export const chatPhotoUrl = (messageId: string, token: string) =>
  `${API_URL}/chat/messages/${messageId}/photo?token=${encodeURIComponent(token)}`;

export const fetchAdminChats = (token: string) =>
  apiFetch<ChatParticipant[]>("/admin/chats", {}, token);

// Task Review queue — all ungraded work across students.
export const fetchPendingWork = (token: string) =>
  apiFetch<PendingWork[]>("/admin/work/pending", {}, token);

export const fetchAdminChatThread = (userId: string, token: string) =>
  apiFetch<ChatMessage[]>(`/admin/chats/${userId}`, {}, token);

export const adminSendChat = (
  userId: string, token: string,
  payload: { body?: string; photo_base64?: string },
) => apiFetch<ChatMessage>(`/admin/chats/${userId}/messages`, { method: "POST", body: JSON.stringify(payload) }, token);

export const adminGradeWork = (messageId: string, status: "passed" | "failed", note: string | undefined, token: string) =>
  apiFetch<ChatMessage>(`/admin/chats/messages/${messageId}/grade`, {
    method: "POST", body: JSON.stringify({ status, note }),
  }, token);

// ── Notifications (pending grade pop-ups) ─────────────────────────────────────

export const fetchNotifications = (token: string) =>
  apiFetch<AppNotification[]>("/notifications", {}, token);

export const ackNotifications = (ids: string[], token: string) =>
  apiFetch("/notifications/ack", {
    method: "POST",
    body: JSON.stringify({ ids }),
  }, token).catch(() => { /* best-effort */ });

// ── Account & Subscription ────────────────────────────────────────────────────

export const fetchAccount = (token: string) =>
  apiFetch<AccountInfo>("/account/me", {}, token);

export const fetchSubscription = (token: string) =>
  apiFetch<Subscription | null>("/account/subscription", {}, token);

// ── Admin: grant premium ──────────────────────────────────────────────────────

export const adminGrantSubscription = (
  body: {
    username?: string; telegram_id?: number; email?: string; days: number; notes?: string;
    /** Which blocks to grant. Omitted = all of them. */
    blocks?: string[];
  },
  token: string,
) =>
  apiFetch<UserAccess>("/admin/subscriptions", {
    method: "POST",
    body: JSON.stringify(body),
  }, token);

// ── Admin ─────────────────────────────────────────────────────────────────────

export const adminReviewSubmission = (
  submissionId: string,
  status: "passed" | "failed",
  note: string | undefined,
  token: string,
) =>
  apiFetch<Submission>(`/admin/submissions/${submissionId}`, {
    method: "PATCH",
    body: JSON.stringify({ status, note }),
  }, token);

export const adminSendMessage = (submissionId: string, body: string, token: string, photoBase64?: string) =>
  apiFetch<SubmissionMessage>(`/admin/submissions/${submissionId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body, photo_base64: photoBase64 }),
  }, token);

// ── Admin inbox ─────────────────────────────────────────────────────────────

export const fetchAdminLessonQuestions = (token: string, onlyPending = true) =>
  apiFetch<AdminLessonQuestion[]>(`/lessons/admin/lesson-questions?only_pending=${onlyPending}`, {}, token);

export const replyAdminLessonQuestion = (progressId: string, body: string, token: string) =>
  apiFetch<AdminLessonQuestion>(`/lessons/admin/lesson-questions/${progressId}/reply`, {
    method: "POST",
    body: JSON.stringify({ body }),
  }, token);

export const fetchAdminInboxSubmissions = (token: string, status = "submitted") =>
  apiFetch<AdminSubmission[]>(`/admin/inbox/submissions?status=${status}`, {}, token);

export const submissionPhotoUrl = (submissionId: string, token: string) =>
  `${API_URL}/admin/submissions/${submissionId}/photo?token=${encodeURIComponent(token)}`;

// ── App configuration (admin-controlled sections, limits, plans) ───────────────

export const fetchAppConfig = (token: string) =>
  apiFetch<AppConfig>("/config", {}, token);

export const fetchAdminSettings = (token: string) =>
  apiFetch<AdminSettings>("/admin/settings", {}, token);

export const updateAdminSettings = (patch: AppSettingsPatch, token: string) =>
  apiFetch<AdminSettings>("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(patch),
  }, token);

export const adminCreatePlan = (body: Omit<Plan, "id">, token: string) =>
  apiFetch<Plan>("/admin/plans", { method: "POST", body: JSON.stringify(body) }, token);

export const adminUpdatePlan = (planId: string, body: Partial<Omit<Plan, "id">>, token: string) =>
  apiFetch<Plan>(`/admin/plans/${planId}`, { method: "PATCH", body: JSON.stringify(body) }, token);

export const adminDeletePlan = (planId: string, token: string) =>
  apiFetch<void>(`/admin/plans/${planId}`, { method: "DELETE" }, token);


// ── Admin: per-block access ─────────────────────────────────────────────────

export const fetchUserAccess = (userId: string, token: string) =>
  apiFetch<UserAccess>(`/admin/users/${userId}/access`, {}, token);

/** Sends the DESIRED state: listed blocks get `days` added, the rest are
 *  revoked. One call, so a half-applied change is not possible. */
export const setUserAccess = (
  userId: string,
  body: { blocks: string[]; days: number },
  token: string,
) => apiFetch<UserAccess>(`/admin/users/${userId}/access`, {
  method: "POST", body: JSON.stringify(body),
}, token);

// ── Invite links ──────────────────────────────────────────────────────────────

export const fetchInvites = (token: string) =>
  apiFetch<InviteList>("/admin/invites", {}, token);

export const createInvite = (
  body: { blocks: string[]; days: number; valid_days: number; note?: string },
  token: string,
) => apiFetch<Invite>("/admin/invites", { method: "POST", body: JSON.stringify(body) }, token);

export const revokeInvite = (id: string, token: string) =>
  apiFetch<Invite>(`/admin/invites/${id}`, { method: "DELETE" }, token);

export const previewInvite = (code: string) =>
  apiFetch<InvitePreview>(`/invites/${encodeURIComponent(code)}`);

export const redeemInvite = (code: string, token: string) =>
  apiFetch<InviteRedeemResult>(`/invites/${encodeURIComponent(code)}/redeem`, { method: "POST" }, token);

// ── Online payment (PayPal, web only) ─────────────────────────────────────────

export const fetchPaymentsConfig = (token: string) =>
  apiFetch<PaymentsConfig>("/payments/config", {}, token);

export const createPaypalOrder = (planId: string, token: string) =>
  apiFetch<{ order_id: string }>("/payments/paypal/orders", {
    method: "POST", body: JSON.stringify({ plan_id: planId }),
  }, token);

export const fetchChatAccess = (token: string) =>
  apiFetch<{ can_write: boolean }>("/chat/access", {}, token);

export const capturePaypalOrder = (orderId: string, token: string) =>
  apiFetch<CaptureResult>(`/payments/paypal/orders/${encodeURIComponent(orderId)}/capture`, { method: "POST" }, token);

export interface AdminPayment {
  id: string;
  provider?: "paypal" | "bepaid_link" | string;
  plan_code: string | null;
  amount: string;
  currency: string;
  status: "created" | "completed" | "failed" | "refunded" | "reversed" | string;
  environment: "sandbox" | "live" | string;
  payer_email: string | null;
  created_at: string;
  completed_at: string | null;
  buyer: string | null;
}

export const fetchAdminPayments = (token: string) =>
  apiFetch<AdminPayment[]>("/admin/payments", {}, token);

// ── Telegram → browser hand-off (pay on the website) ──────────────────────────

export const createHandoff = (token: string) =>
  apiFetch<{ token: string; expires_at: string }>("/auth/handoff", { method: "POST" }, token);

/** Whose account a hand-off link opens — asked before switching to it. */
export const peekHandoff = (handoffToken: string) =>
  apiFetch<{ name: string; username: string | null }>("/auth/handoff/peek", {
    method: "POST", body: JSON.stringify({ token: handoffToken }),
  });

export const redeemHandoff = (handoffToken: string) =>
  apiFetch<{ access_token: string }>("/auth/handoff/redeem", {
    method: "POST", body: JSON.stringify({ token: handoffToken }),
  });

// ── «Сообщить о проблеме» ─────────────────────────────────────────────────────

export const sendReport = (
  body: {
    category: "bug" | "unclear" | "payment" | "other";
    message: string;
    photo_base64?: string;
    context: Record<string, string | undefined>;
  },
  token: string,
) => apiFetch<{ ok: boolean; delivered: boolean }>("/reports", { method: "POST", body: JSON.stringify(body) }, token);

export const fetchQuotes = (token: string) =>
  apiFetch<PlanQuote[]>("/payments/quotes", {}, token);

// ── bePaid checkout (automatic) ─────────────────────────────────────────────

export const createBepaidOrder = (planId: string, token: string, language: string) =>
  apiFetch<{ payment_id: string; redirect_url: string }>("/payments/bepaid/orders", {
    method: "POST", body: JSON.stringify({ plan_id: planId, origin: window.location.origin, language }),
  }, token);

export const fetchBepaidStatus = (paymentId: string, token: string) =>
  apiFetch<{ status: string; plan_code: string | null; amount: string; currency: string }>(
    `/payments/bepaid/${paymentId}`, {}, token);

/** A rouble price in BYN, rounded like the server (whole roubles, half up). */
export function bynOf(rub: number | string, ratePer100 = 3.66): number {
  return Math.round((Number(rub) * Math.round(ratePer100 * 10000)) / 1000000);
}

// ── Payment by link (bePaid product page) — confirmed by the admin ────────────

export const startLinkPayment = (planId: string, token: string) =>
  apiFetch<{ payment_id: string; url: string }>("/payments/link/start", {
    method: "POST", body: JSON.stringify({ plan_id: planId }),
  }, token);

export const claimLinkPayment = (paymentId: string, token: string) =>
  apiFetch<{ status: string }>(`/payments/link/${paymentId}/claim`, { method: "POST" }, token);

export const adminConfirmPayment = (paymentId: string, token: string) =>
  apiFetch<AdminPayment>(`/admin/payments/${paymentId}/confirm`, { method: "POST" }, token);

export const adminRejectPayment = (paymentId: string, token: string) =>
  apiFetch<AdminPayment>(`/admin/payments/${paymentId}/reject`, { method: "POST" }, token);

// ── Admin: users list ─────────────────────────────────────────────────────────

export const fetchAdminUsers = (
  params: { q: string; filter: string; sort: string; offset: number; limit?: number }, token: string,
) => {
  const qs = new URLSearchParams({
    q: params.q, filter: params.filter, sort: params.sort,
    offset: String(params.offset), limit: String(params.limit ?? 30),
  });
  return apiFetch<AdminUsersPage>(`/admin/users?${qs}`, {}, token);
};

export const fetchAdminUser = (id: string, token: string) =>
  apiFetch<AdminUserDetail>(`/admin/users/${id}`, {}, token);

export const fetchAdminSelections = (
  params: { q: string; offset: number; userId?: string; limit?: number }, token: string,
) => {
  const qs = new URLSearchParams({ q: params.q, offset: String(params.offset), limit: String(params.limit ?? 30) });
  if (params.userId) qs.set("user_id", params.userId);
  return apiFetch<AdminSelectionsPage>(`/admin/selections?${qs}`, {}, token);
};

export const fetchAdminSelection = (number: number, token: string) =>
  apiFetch<AdminSelectionDetail>(`/admin/selections/${number}`, {}, token);

export const adminGrantBlocks = (userId: string, blocks: string[], days: number, token: string) =>
  apiFetch<AdminUserRow>(`/admin/users/${userId}/grant`, {
    method: "POST", body: JSON.stringify({ blocks, days }),
  }, token);

/** blocks undefined = cancel every block. */
export const adminRevokeBlocks = (userId: string, blocks: string[] | undefined, token: string) =>
  apiFetch<AdminUserRow>(`/admin/users/${userId}/revoke`, {
    method: "POST", body: JSON.stringify({ blocks: blocks ?? null }),
  }, token);

export const adminDeleteUser = (userId: string, token: string) =>
  apiFetch<{ status: string }>(`/admin/users/${userId}`, { method: "DELETE" }, token);
